import { Inject, Injectable } from '@angular/core';
import { SCOPE_KEY } from '../tokens';

import { config } from '../../../config';

/**
 * Session data payload
 */
interface Payload {
  [key: string]: string;
}

/**
 * Session data storage and management
 */
@Injectable()
export class SessionService {
  private payload: Payload;
  private storageKey: string;

  constructor(@Inject(SCOPE_KEY) scopeKey: string) {
    this.storageKey = `${scopeKey}_session`;
    this.payload = this.hydrate();
  }

  /**
   * Get a key from the session payload
   *
   * @param {string} key
   * @param {any} def Default value if the key doesn't exist
   * @return {any}
   */
  get(key: string, def?: any): any {
    return this.has(key) ? this.payload[key] : def;
  }

  /**
   * Check if a key exists in the session payload
   *
   * @param {string} key
   * @return {boolean}
   */
  has(key: string): boolean {
    return key in this.payload;
  }

  /**
   * Set a key in the session payload
   *
   * @param {string} key
   * @param {any} value
   * @return {void}
   */
  set(key: string, value: any): void {
    this.payload[key] = value;
    this.sync(this.payload);
  }

  /**
   * Empty the session
   *
   * @return {void}
   */
  clear(): void {
    this.set(this.storageKey, undefined);
    this.set(config.PAYMENT_DETAILS, undefined);
  }

  /**
   * Get the payload from storage
   *
   * @private
   * @return {Payload}
   */
  private hydrate(): Payload {
    const rawPayload = localStorage.getItem(this.storageKey);

    if (rawPayload) {
      try {
        return JSON.parse(rawPayload);
      } catch (e) {
        // Clear corrupt payload from persistant storage
        this.sync({});
      }
    }

    return {};
  }

  /**
   * Sync the payload to storage
   *
   * @private
   * @param {Payload} payload
   * @return {void}
   */
  private sync(payload?: Payload): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(payload));
    } catch (e) {
      // Unable to save to persistant local storage
      // @TODO Alert user they won't be able to remain signed in our answers highlightened.
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/core/services/session.service.ts